import type { Advisor } from '@/types'

import { availabilityTags } from '@/lib/schedule'

export const GOALS = [
    { id: 'budgeting', label: 'Take charge of spending and debt', detail: 'Clear what you owe without giving up the things you enjoy', specialty: 'Budgeting and debt', related: ['Self-employed', 'Home buying'] },
    { id: 'investing', label: 'Begin investing, or invest better', detail: 'Low-cost portfolios explained in plain words', specialty: 'Investing', related: ['Retirement', 'Tax planning'] },
    { id: 'retirement', label: 'Retire on my own terms', detail: 'Steady income, drawn at the right pace', specialty: 'Retirement', related: ['Family wealth', 'Tax planning'] },
    { id: 'home', label: 'Buy a first home', detail: 'Down payment, mortgage and a payment that fits', specialty: 'Home buying', related: ['Budgeting and debt'] },
    { id: 'tax', label: 'Lower my taxes, by the book', detail: 'Calm planning all year, not a rush each spring', specialty: 'Tax planning', related: ['Self-employed', 'Retirement'] },
    { id: 'self-employed', label: 'Steady my freelance or business income', detail: 'Pay yourself regularly, even in lean months', specialty: 'Self-employed', related: ['Tax planning', 'Budgeting and debt'] },
    { id: 'family', label: 'Look after wealth for the next generation', detail: 'Wills, trusts and handing things on with care', specialty: 'Family wealth', related: ['Retirement', 'Tax planning'] }
] as const

export const STAGES = [
    { id: 'starting', label: 'Finding my footing', detail: 'Setting up the basics and steady habits', minYears: 0 },
    { id: 'momentum', label: 'Gaining ground', detail: 'Putting money aside every month', minYears: 5 },
    { id: 'established', label: 'Well underway', detail: 'More accounts, more goals to balance', minYears: 10 },
    { id: 'next', label: 'Looking to what comes next', detail: 'Retiring, selling up or leaving a legacy', minYears: 15 }
] as const

export const TIMES = ['Mornings', 'Afternoons', 'Evenings', 'Weekends', 'Any time'] as const
export const FORMATS = ['Video', 'Phone', 'In person', 'No preference'] as const

export type GoalId = typeof GOALS[number]['id']
export type StageId = typeof STAGES[number]['id']
export type TimePreference = typeof TIMES[number]
export type FormatPreference = typeof FORMATS[number]

export interface MatchAnswers {
    goal: GoalId
    stage: StageId
    time: TimePreference
    format: FormatPreference
}

export interface AdvisorMatch {
    advisor: Advisor
    score: number
    reasons: Array<string>
}

export const matchAdvisors = (advisors: Array<Advisor>, answers: MatchAnswers): Array<AdvisorMatch> => {
    const goal = GOALS.find(item => item.id === answers.goal) ?? GOALS[0]
    const stage = STAGES.find(item => item.id === answers.stage) ?? STAGES[0]

    return advisors
        .map(advisor => {
            const reasons: Array<string> = []
            let points = 0

            if (advisor.specialty === goal.specialty) {
                points += 60
                reasons.push(`Focuses on ${advisor.specialty.toLowerCase()}`)
            }
            else if ((goal.related as ReadonlyArray<string>).includes(advisor.specialty)) {
                points += 28
                reasons.push(`Well versed in ${advisor.specialty.toLowerCase()}`)
            }

            if (advisor.yearsExperience >= stage.minYears) {
                points += 15
                reasons.push(`${advisor.yearsExperience} years advising clients`)
            }
            else {
                points -= 8
            }

            const tags = availabilityTags(advisor)

            if (answers.time === 'Any time' || tags.includes(answers.time)) {
                points += 12
                if (answers.time !== 'Any time') reasons.push(`Open ${answers.time.toLowerCase()}`)
            }

            if (answers.format === 'No preference' || advisor.meetingFormats.includes(answers.format)) {
                points += 8
                if (answers.format !== 'No preference') reasons.push(answers.format === 'In person' ? 'Happy to meet in person' : `Takes ${answers.format.toLowerCase()} calls`)
            }
            else {
                points -= 6
            }

            points += advisor.rating

            return { advisor, score: Math.max(55, Math.min(98, Math.round(points))), reasons }
        })
        .sort((a, b) => b.score - a.score || b.advisor.rating - a.advisor.rating)
}
